import * as secrets from "@aws-sdk/client-secrets-manager";
import { safeParse } from "../common/json";

let secretsCache: Record<string, Record<string, string | undefined>> | undefined;
/**
 * Calls AWS SecretsManager to retrieve the given secret,
 * caching the value globally in-memory if the cache has been initialized.
 * @param secretId
 * @returns
 */
async function _getSecretManagerSecrets(secretId: string) {
  if (secretsCache && secretsCache[secretId]) return secretsCache[secretId];
  const client = new secrets.SecretsManagerClient({});
  try {
    const secret = safeParse((await client.send(new secrets.GetSecretValueCommand({
      SecretId: secretId
    }))).SecretString!) as Record<string, string | undefined>
    if (secretsCache) secretsCache[secretId] = secret;
    return secret;
  } finally {
    client.destroy();
  }
}
/**
 * Initializes the global in-memory secrets cache for use if it does not exist
 * or clears its contents if it has already been initialized.
 */
export function initSecretsCache() {secretsCache = {}};

export async function getGlobalConfig(key: string): Promise<string | undefined> {
  if (process.env[key] && (['development','test'].includes(process.env.NODE_ENV || '') || ['ENCRYPTION_KEY','HASH_KEY'].indexOf(key) === -1)) {
    return process.env[key];
  }

  if (process.env.NODE_ENV === 'development') return undefined;

  try {
    const resp = await _getSecretManagerSecrets('LambdaKeys')
    const result = resp[key];
    return result;
  } catch (e) {
    if (!['development','test'].includes(process.env.NODE_ENV || '')) console.warn("Unable to get secret:", key, e);
    return undefined;
  }
}

export async function getGlobalConfigs<T extends string>(...keys: T[]): Promise<Record<T, string | undefined>> {
  return await getSecrets('LambdaKeys', keys);
}

export async function getSecrets<T extends string>(secretId: string, keys: T[]): Promise<Record<T, string | undefined>> {
  const result: Record<string, string | undefined> = {};

  const needToFetch: string[] = [];
  for (const key of keys) {
    if (process.env[key]) {
      result[key] = process.env[key];
      continue;
    }

    needToFetch.push(key);
  }

  if (process.env.NODE_ENV !== 'development' && needToFetch.length) {
    try {
      const secretJson = await _getSecretManagerSecrets(secretId);
      for (const k in secretJson) {
        if (!result[k]) {
          result[k] = secretJson[k];
        }
      }
    } catch (e) {
      if (!['development','test'].includes(process.env.NODE_ENV || '')) console.warn("Unable to get secrets:", JSON.stringify(keys), e);
      Object.assign(result, {
        ...result,
        ...needToFetch.reduce((acc, key) => {
          if (!result[key]) {
            acc[key] = undefined;
          }
          return acc;
        }, {} as Record<string, undefined>)
      });
    }
  }

  return result;
}

const DATADOG_API_KEY_SECRET_ID = 'DdApiKeySecret-ebttFbv8Xify';
let cachedDatadogApiKey: string | null = null;

export async function getDatadogApiKey(): Promise<string> {
  if (!cachedDatadogApiKey) {
    const client = new secrets.SecretsManagerClient({});

    try {
      const output = await client.send(new secrets.GetSecretValueCommand({
        SecretId: DATADOG_API_KEY_SECRET_ID,
      }));

      if (!output.SecretString) {
        throw new Error('Could not retrieve Datadog API key');
      }

      cachedDatadogApiKey = output.SecretString;
    } finally {
      client.destroy();
    }
  }

  return cachedDatadogApiKey;
}

export async function getSecretBinary(secretId: string): Promise<Buffer | string> {

  if (process.env[secretId]) {
    return process.env[secretId]!;
  }

  const client = new secrets.SecretsManagerClient({});

  try {
    const output = await client.send(new secrets.GetSecretValueCommand({
      SecretId: secretId
    }));
    if (!output.SecretBinary) {
      throw new Error('missing configuration for ' + secretId);
    }
    // output.SecretBinary is a Uint8Array encoded in base64, so we stringify it
    // and pass it to another Buffer.from to convert from base64 to a utf-8 encoded buffer
    return Buffer.from(Buffer.from(output.SecretBinary).toString('base64'), 'base64');
  } finally {
    client.destroy();
  }

}

export async function updateSecretBinary(secretId: string, secretBinary: Uint8Array) {
  const client = new secrets.SecretsManagerClient({});

  try {
    await client.send(new secrets.UpdateSecretCommand({
      SecretId: secretId,
      SecretBinary: secretBinary,
    }));
  } finally {
    client.destroy();
  }

}
